import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getLocaleFromPathname } from '../../i18n/locale';
import { getHomePageCopy } from '../../i18n/homePage';
import '../../css/HomePage.css';

const TRUST_ICONS = [
    <svg key="field" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>,
    <svg key="account" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></svg>,
    <svg key="adopt" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>,
];

const CONNECTED_ICONS = [
    <svg key="platform" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>,
    <svg key="reports" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
    <svg key="issues" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>,
    <svg key="audit" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>,
];

const ROLE_META = [
    {
        color: 'orange',
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    },
    {
        color: 'blue',
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></svg>,
    },
    {
        color: 'purple',
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
    },
    {
        color: 'green',
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    },
] as const;

export const HomePage = () => {
    const location = useLocation();
    const locale = getLocaleFromPathname(location.pathname);
    const copy = getHomePageCopy(locale);
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    return (
        <div className="home-page">
            {/* Hero Section */}
            <section className="home-hero" style={{ backgroundImage: 'url(/homepage-images/navbar-header-overlay-image.png)' }}>
                <div className="home-hero-container">
                    <div className="home-hero-left">
                        <div className="home-hero-badge">
                            <span>🔍</span> {copy.hero.badge}
                        </div>
                        <h1 className="home-hero-title">
                            {copy.hero.title}
                        </h1>
                        <p className="home-hero-description">
                            {copy.hero.description}
                        </p>
                        <div className="home-hero-buttons">
                            <Link to="/register/free-demo" className="splash-btn-primary">
                                {copy.hero.startFreeDemo}
                            </Link>
                            <Link to="/contact" className="splash-btn-outline">
                                {copy.hero.contactUs} <span>→</span>
                            </Link>
                        </div>
                    </div>
                    <div className="home-hero-right">
                        <img
                            src="/homepage-images/header-image-splash.png"
                            alt={copy.hero.heroImageAlt}
                            className="home-hero-image"
                        />
                    </div>
                </div>
            </section>

            {/* Trust / Value Props */}
            <section className="home-trust">
                <div className="home-trust-container">
                    <h2>{copy.trust.title}</h2>
                    <p className="home-trust-subtitle">
                        {copy.trust.subtitle}
                    </p>
                    <div className="home-trust-grid">
                        {copy.trust.cards.map((card, index) => (
                            <div key={card.title} className="home-trust-card">
                                <div className="home-trust-icon">
                                    {TRUST_ICONS[index]}
                                </div>
                                <h3>{card.title}</h3>
                                <p>{card.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Problems Section */}
            <section className="home-problems">
                <div className="home-problems-container">
                    <div className="home-problems-image">
                        <img src="/homepage-images/illustration.png" alt={copy.problems.imageAlt} />
                    </div>
                    <div className="home-problems-content">
                        <h2>{copy.problems.title}</h2>
                        <div className="home-problems-grid">
                            {copy.problems.cards.map((card) => (
                                <div key={card.title} className="home-problem-card">
                                    <div className="home-problem-icon orange">{card.icon}</div>
                                    <h4>{card.title}</h4>
                                    <p>{card.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Everything Connected Section */}
            <section className="home-connected">
                <div className="home-connected-container">
                    <h2>{copy.connected.title}</h2>
                    <p className="home-connected-subtitle">
                        {copy.connected.subtitle}
                    </p>
                    <div className="home-connected-grid">
                        {copy.connected.cards.map((card, index) => (
                            <div key={card.title} className="home-connected-card">
                                <div className="home-connected-icon">
                                    {CONNECTED_ICONS[index]}
                                </div>
                                <h3>{card.title}</h3>
                                <p>{card.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Core Modules Section */}
            <section className="home-modules">
                <div className="home-modules-container">
                    <h2>{copy.modules.sectionTitle}</h2>
                    <p className="home-modules-subtitle">{copy.modules.sectionSubtitle}</p>

                    <div className="home-addons-header">
                        <h2>
                            <span className="orange-text">{copy.modules.coreHeader.highlight}</span>
                            {copy.modules.coreHeader.rest}
                        </h2>
                        <p>{copy.modules.coreHeader.subtitle}</p>
                    </div>

                    {copy.modules.core.map((module, index) => (
                        <div key={module.title} className={`home-module-row${index % 2 === 1 ? ' reverse' : ''}`}>
                            <div className="home-module-image">
                                <img src={copy.modules.coreImages[index]} alt={module.imageAlt} />
                            </div>
                            <div className="home-module-content">
                                <span className="splash-section-label">{module.label}</span>
                                <h3>{module.title}</h3>
                                <p>{module.body}</p>
                                <ul className="splash-bullet-list">
                                    {module.bullets.map((bullet) => (
                                        <li key={bullet}>{bullet}</li>
                                    ))}
                                </ul>
                                <Link to="/features" className="splash-btn-learn-more">{copy.modules.learnMore}</Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Add-On Modules Section */}
            <section className="home-addons">
                <div className="home-addons-container">
                    <div className="home-addons-header">
                        <h2>
                            <span className="orange-text">{copy.modules.addonHeader.highlight}</span>
                            {copy.modules.addonHeader.rest}
                        </h2>
                        <p>{copy.modules.addonHeader.subtitle}</p>
                    </div>

                    {copy.modules.addons.map((module, index) => (
                        <div key={module.title} className={`home-module-row${index % 2 === 1 ? ' reverse' : ''}`}>
                            <div className="home-module-image">
                                <img src={copy.modules.addonImages[index]} alt={module.imageAlt} />
                            </div>
                            <div className="home-module-content">
                                <span className="splash-section-label">{module.label}</span>
                                <h3>{module.title}</h3>
                                <p>{module.body}</p>
                                <ul className="splash-bullet-list">
                                    {module.bullets.map((bullet) => (
                                        <li key={bullet}>{bullet}</li>
                                    ))}
                                </ul>
                                <Link to="/features" className="splash-btn-learn-more">{copy.modules.learnMore}</Link>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing Teaser */}
            <section className="home-pricing-teaser" style={{ backgroundImage: 'url(/homepage-images/Articles.png)' }}>
                <div className="home-pricing-container">
                    <div className="home-pricing-content">
                        <h2>{copy.pricing.title}</h2>
                        <p>{copy.pricing.body}</p>
                        <div className="home-pricing-buttons">
                            <Link to="/register/free-demo" className="splash-btn-primary">{copy.pricing.startFreeDemo}</Link>
                            <Link to="/contact" className="splash-btn-outline">{copy.pricing.contactQuote} <span>→</span></Link>
                        </div>
                    </div>
                    <div className="home-pricing-image">
                        <img src="/homepage-images/Right.png" alt={copy.pricing.imageAlt} />
                    </div>
                </div>
            </section>

            {/* 30-Day Free Trial */}
            <section className="home-trial">
                <div className="home-trial-container">
                    <div className="home-trial-content">
                        <h2>{copy.trial.title}</h2>
                        <p>{copy.trial.body}</p>
                        <Link to="/register/free-demo" className="splash-btn-primary">{copy.trial.cta}</Link>
                    </div>
                    <div className="home-trial-image">
                        <img src="/homepage-images/Cta.png" alt={copy.trial.imageAlt} />
                    </div>
                </div>
            </section>

            {/* Built for Everyone */}
            <section className="home-roles">
                <div className="home-roles-container">
                    <h2>{copy.roles.title}</h2>
                    <div className="home-roles-grid">
                        {copy.roles.cards.map((card, index) => (
                            <div key={card.title} className="home-role-card">
                                <div className={`home-role-icon ${ROLE_META[index].color}`}>
                                    {ROLE_META[index].icon}
                                </div>
                                <h4>{card.title}</h4>
                                <ul>
                                    {card.bullets.map((bullet) => (
                                        <li key={bullet}>{bullet}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="home-testimonials">
                <div className="home-testimonials-container">
                    <span className="splash-section-label">{copy.testimonials.label}</span>
                    <h2>{copy.testimonials.title}</h2>
                    <div className="home-testimonials-grid">
                        {copy.testimonials.quotes.map((quote, index) => (
                            <div
                                key={quote}
                                className={`home-testimonial-card${index === 1 ? ' highlighted' : ''}`}
                            >
                                <p className="home-testimonial-quote">{quote}</p>
                                <div className="home-testimonial-author">
                                    <div className="home-testimonial-avatar"></div>
                                    <div className="home-testimonial-info">
                                        <strong>{copy.testimonials.authorPlaceholder}</strong>
                                        <span>{copy.testimonials.rolePlaceholder}</span>
                                    </div>
                                    <div className="home-testimonial-rating">★★★★★ <span>{copy.testimonials.ratingsLabel}</span></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="home-testimonials-nav">
                        <button className="home-testimonial-nav-btn" aria-label={copy.testimonials.prevAria}>←</button>
                        <button className="home-testimonial-nav-btn active" aria-label={copy.testimonials.nextAria}>→</button>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="home-faq">
                <div className="home-faq-container">
                    <span className="splash-section-label">{copy.faq.label}</span>
                    <h2>{copy.faq.title}</h2>
                    <div className="home-faq-list">
                        {copy.faq.items.map((faq, index) => (
                            <div key={faq.question} className={`home-faq-item ${openFaq === index ? 'open' : ''}`}>
                                <button
                                    className="home-faq-question"
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    aria-expanded={openFaq === index}
                                >
                                    {faq.question}
                                    <span className="home-faq-toggle">{openFaq === index ? '−' : '+'}</span>
                                </button>
                                {openFaq === index && (
                                    <div className="home-faq-answer">{faq.answer}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="home-final-cta">
                <div className="home-final-cta-container">
                    <div className="home-final-cta-content">
                        <h2>{copy.finalCta.title}</h2>
                        <p>{copy.finalCta.body}</p>
                        <div className="home-final-cta-buttons">
                            <Link to="/register/free-demo" className="splash-btn-primary">{copy.finalCta.startFreeDemo}</Link>
                            <Link to="/contact" className="splash-btn-outline">{copy.finalCta.contactUs} <span>→</span></Link>
                        </div>
                    </div>
                    <div className="home-final-cta-image">
                        <img src="/homepage-images/Cta.png" alt={copy.finalCta.imageAlt} />
                    </div>
                </div>
            </section>
        </div>
    );
};
